import React, { useContext, createContext } from 'react'
import { gql } from 'apollo-boost'
import { useMutation } from '@apollo/react-hooks'

interface AssetDeliveryContext {
  createAssets(productId: string, assets: any[]): Promise<void>
  updateAsset(asset: any): Promise<void>
  deleteAsset(asset: any): Promise<void>
  deleteAssetDeliveriesByProduct(productId: string): Promise<any>
}

interface AssetDeliveryProps {
  children: any
}

const AssetDeliveryContext = createContext<AssetDeliveryContext>({
  createAssets: (productId, assets) => Promise.resolve(),
  updateAsset: (asset) => Promise.resolve(),
  deleteAsset: (asset) => Promise.resolve(),
  deleteAssetDeliveriesByProduct: (productId) => Promise.resolve()
})

export const assetFragment = gql`
  fragment Asset on Asset {
    id
    ...on UrlAsset {
      url
    }
    ...on PhotoAsset {
      labels
      isPrimary
      groupId
      masterForGroupId
      image {
        url
        thumbUrl
      }
      comment
    }
  }
`

export const productFragment = gql`
  fragment Product on Product {
    id
    ...on BookableProduct {
      assetDeliveryType
      assetDeliveries {
        ...Asset
      }
    }
  }
  ${assetFragment}
`

const createAssetsMutation = gql`
  mutation CreateAssetsDelivery($input: CreateAssetsDeliveryInput!) {
    CreateAssetsDelivery(input: $input) {
      product {
        ...Product
      }
      assets {
        uploadUrl
        md5
        asset {
          ...Asset
        }
      }
    }
  }
  ${assetFragment}
  ${productFragment}
`

const updateAssetMutation = gql`
  mutation UpdateAssetDelivery($input: UpdateAssetDeliveryInput!) {
    UpdateAssetDelivery(input: $input) {
      ...Asset
    }
  }
  ${assetFragment}
`

const deleteAssetMutation = gql`
  mutation DeleteAssetDelivery($id: ID!) {
    DeleteAssetDelivery(id: $id) {
      product {
        ...Product
      }
    }
  }
  ${productFragment}
`

const deleteAssetDeliveriesByProductMutation = gql`
  mutation DeleteAssetDeliveriesByProduct($productId: ID!) {
    DeleteAssetDeliveriesByProduct(productId: $productId) {
      deleteResults {
        id
        wasDeleted
        error
      }
      product {
        ...Product
      }
    }
  }
  ${productFragment}
`

export default function AssetDeliveryProvider({ children }: AssetDeliveryProps) {
  const [createAssets] = useMutation(createAssetsMutation)
  const [updateAsset] = useMutation(updateAssetMutation)
  const [deleteAsset] = useMutation(deleteAssetMutation)
  const [deleteAssetDeliveriesByProduct] = useMutation(deleteAssetDeliveriesByProductMutation)

  function handleCreateAsset(productId: string, assets: any[]): Promise<any> {
    return createAssets({
      variables: { input: { productId, assets } },
    }).then((res: any) => res.data.CreateAssetsDelivery.assets)
  }

  function handleUpdateAsset(asset: any) {
    return updateAsset({
      variables: { input: asset },
    }).then((res: any) => res.data.UpdateAssetDelivery)
  }

  function handleDeleteAsset(asset: any) {
    return deleteAsset({
      variables: { id: asset.id },
    }).then((res: any) => res.data.DeleteAssetDelivery)
  }

  function handleDeleteAssetDeliveriesByProduct(productId: string) {
    return deleteAssetDeliveriesByProduct({
      variables: { productId },
    }).then((res: any) => res.data.DeleteAssetDeliveriesByProduct)
  }

  const context = {
    createAssets: handleCreateAsset,
    updateAsset: handleUpdateAsset,
    deleteAsset: handleDeleteAsset,
    deleteAssetDeliveriesByProduct: handleDeleteAssetDeliveriesByProduct
  }

  return (
    <AssetDeliveryContext.Provider value={context}>
      {children}
    </AssetDeliveryContext.Provider>
  )
}

export function useAssetDelivery() {
  return useContext(AssetDeliveryContext)
}
